/**
 * #1106 production rich-text editor (web) — Tiptap, Teams/Loop-class toolbar
 * ceiling. See `TextAreaEditor.web.tsx` for the interface this satisfies.
 */
import { EditorContent, useEditor } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { LinkApplyButton } from './LinkApplyButton';
import {
  acceptPasteTransaction,
  appendImagesAfterLastTable,
  htmlHasUnresolvedOutlookImages,
  previewsToPlaceUnderTable,
  htmlToStitchOnPaste,
  isBlankPasteHtml,
  isImageOnlyPasteHtml,
  pasteHtmlContainsEmbeddedImage,
  readMixedEmailPaste,
  shouldDropClipboardSnapshotBesideTable,
  shouldInsertClipboardImageFiles,
  supplementOutlookClipboardImages,
} from './mixedEmailPaste.web';
import {
  editorHtmlHasRehostableImages,
  isRehostableImageSrc,
  rehostInlineImagesInEditor,
} from './rehostInlineImages.web';
import {
  collectImageSrcAttrs,
  restoreImageSrcsFromAttrs,
} from './restoreImageSrcsFromAttrs';
import { richHtmlScopedCss } from './richHtmlVisualContract';
import {
  autoLinkMatch,
  linkEditorRange,
  linkUrlsInSlice,
} from './richTextAutoLink';
import { RICH_TEXT_WEB_SCROLLPORT_STYLE } from './richTextWideContent';
import { recoverWordTablePaste } from './rtfTableToHtml';
import {
  normalizeRichListStructure,
  promoteEmailImageSlots,
  sanitizePaste,
} from './sanitizePaste';
import { shouldReplaceEditorHtml } from './shouldReplaceEditorHtml';
import {
  SiteScopeTable,
  SiteScopeTableCell,
  SiteScopeTableHeader,
} from './siteScopeTable';
import {
  clipboardFlavorTypes,
  clipboardImgSrcSchemes,
  dropImgsCoveredByClipboardFiles,
  isLocalPasteHarnessHost,
  stitchClipboardImagesIntoHtml,
  summarizeImgSrcSchemes,
  type PasteHarnessSnapshot,
} from './stitchClipboardImages';
import { stripEmptyListItemsFromSlice } from './stripPastedEmptyListItems';
import { buildWebEditorExtensions } from './webEditorExtensions';
import { INLINE_PHOTO_ACCEPT_ATTR } from '../../constants/inlineImageMimeTypes';
import {
  parkImgSrcsForDomParse,
  rememberPendingInlineImagePreview,
  rewriteHtmlPreviewSrcsToPendingRefs,
} from '../../services/pendingInlineImagePreview';
import { colors } from '../../theme/colors';
import { fontSize, lineHeight } from '../../theme/typography';
import {
  beginInlineImageAttempt,
  endInlineImageAttempt,
} from '../../utils/richContent/inlineAttachmentName';
import { sanitizeTextArea } from '../../utils/richContent/sanitizeTextArea';
import { utf8ByteLength } from '../../utils/richContent/textAreaLength';
import {
  TextAreaToolbar,
  type TextAreaToolbarLayout,
  type TextAreaToolbarSet,
} from '../textArea/TextAreaToolbar';

import type { InlineImageResolution } from '../textArea/inlineImage.types';
import type { Editor } from '@tiptap/core';

/** Upload lifecycle callbacks for save gating and diagnostics. */
type InlineImageUploadStatus =
  | { status: 'idle' }
  | { status: 'uploading'; clientPasteId: string }
  | {
      status: 'resolved';
      clientPasteId: string;
      attachmentId?: number;
      reference: string;
    }
  | { status: 'error'; clientPasteId: string; message: string };

interface RichTextEditorProps {
  /**
   * Persists image bytes and hands back the `src` to insert. Injected by the
   * caller so this control never learns which request it belongs to, which
   * section it writes into, or whether the app is online.
   */
  onResolveImage: (file: File) => Promise<InlineImageResolution>;
  initialHtml?: string;
  /** Lab diagnostics (init timing, paste notes). Off for production fields. */
  showDiagnostics?: boolean;
  onStatusChange?: (status: InlineImageUploadStatus) => void;
  onHtmlChange?: (html: string) => void;
  placeholder?: string;
  /** Tracks upload/staging through editor insert so save can wait for completion. */
  inlineImageFieldKey?: string;
  /**
   * Toolbar layout override. Web defaults to 'full' (wraps in narrow hosts);
   * 'compact' renders the grouped one-row toolbar (native's default).
   */
  toolbarLayout?: TextAreaToolbarLayout;
  /**
   * Which button set the production toolbar offers (#1530 Teams parity).
   * `message` shapes the expanded row of the `expandable` layout; default
   * `full` is the request-field document set.
   */
  toolbarSet?: TextAreaToolbarSet;
  /**
   * When true, the editor absorbs leftover height in a flex column host
   * (text-intake modal, #1149 Issue 5): frame + scrollport become flex:1 /
   * minHeight:0 and the default maxHeight:420 is cleared so only the
   * scrollport scrolls. Default path is unchanged for form fields.
   */
  fillHeight?: boolean;
  /**
   * Ctrl/⌘+Enter inside the editor (#1530). Registered in Tiptap's
   * `editorProps.handleKeyDown`, so a chord in another field never reaches
   * it; the event is consumed so ProseMirror does not also split the block.
   * Plain Enter is untouched. No-op unless provided.
   */
  onSubmitShortcut?: () => void;
  /**
   * Read-only switch (#1530, review 2026-10-01). Defaults to true. Seeds
   * Tiptap's `editable` and is re-applied through `editor.setEditable` on
   * every change, so a composer that flips it for the length of a send does
   * not rebuild the editor (which would drop the document).
   */
  editable?: boolean;
}

type InitMetrics = {
  initializedInMs: number;
  contentBytes: number;
};

function persistableHtmlFromEditor(editor: Editor): string | null {
  const raw = restoreImageSrcsFromAttrs(
    editor.getHTML(),
    collectImageSrcAttrs(editor.state.doc),
  );
  // Do not emit a blob-stripped document while rehost is still in flight —
  // that is the silent image loss on fast save (review #2).
  if (editorHtmlHasRehostableImages(raw)) {
    return null;
  }
  const html = rewriteHtmlPreviewSrcsToPendingRefs(raw);
  return html.replace(/<img\b[^>]*\bsrc=(["'])blob:[^"']+\1[^>]*>/gi, '');
}

function emitPersistableEditorHtml(
  editor: Editor,
  onHtmlChange: ((html: string) => void) | undefined,
  lastEmittedHtmlRef: React.MutableRefObject<string>,
): void {
  const persistable = persistableHtmlFromEditor(editor);
  if (persistable == null) {
    return;
  }
  if (persistable.includes('[Uploading image:')) {
    return;
  }
  lastEmittedHtmlRef.current = persistable;
  onHtmlChange?.(persistable);
}

type ToolbarButtonProps = {
  testID: string;
  label: string;
  accessibilityLabel: string;
  active?: boolean;
  disabled?: boolean;
  onPress: () => void;
  colorSwatch?: string;
};

// Email layout tables stay borderless. Bordered Word/Excel/Docs grids are kept
// and marked data-ss-grid. Toolbar insert-table uses the same table node.
const RICH_TEXT_PASTE_OPTIONS = {
  tablePolicy: 'keep-data',
  allowCodeBlocks: true,
} as const;

const FONT_SIZE_PRESETS = ['12px', '14px', '16px', '18px', '24px'] as const;

const FONT_FAMILY_PRESETS = [
  { id: 'sans', value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { id: 'serif', value: 'Georgia, Times, serif', label: 'Georgia' },
  { id: 'mono', value: 'Courier New, monospace', label: 'Mono' },
] as const;

const TEXT_COLORS = [
  { id: 'black', value: '#111827', label: 'Black' },
  { id: 'red', value: '#DC2626', label: 'Red' },
  { id: 'blue', value: '#2563EB', label: 'Blue' },
  { id: 'green', value: '#059669', label: 'Green' },
  { id: 'orange', value: '#D97706', label: 'Orange' },
] as const;

const HIGHLIGHT_COLORS = [
  { id: 'yellow', value: '#FEF08A', label: 'Yellow highlight' },
  { id: 'green', value: '#BBF7D0', label: 'Green highlight' },
  { id: 'blue', value: '#BFDBFE', label: 'Blue highlight' },
] as const;

type ToolbarVariant = 'production' | 'lab';

function toolbarTestId(variant: ToolbarVariant, id: string): string {
  if (variant === 'production') {
    return id === 'toolbar' ? 'pd-richtext-toolbar' : `pd-richtext-${id}`;
  }
  return `pd-richtext-spike-first-${id}`;
}

function ToolbarButton({
  testID,
  label,
  accessibilityLabel,
  active = false,
  disabled = false,
  onPress,
  colorSwatch,
}: ToolbarButtonProps): React.JSX.Element {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected: active, disabled }}
      disabled={disabled}
      onPress={onPress}
      {...({
        onMouseDown: (event: { preventDefault: () => void }) => {
          event.preventDefault();
        },
      } as object)}
      style={[
        styles.toolbarButton,
        active && styles.toolbarButtonActive,
        disabled && styles.toolbarButtonDisabled,
      ]}
    >
      {colorSwatch ? (
        <View style={[styles.colorSwatch, { backgroundColor: colorSwatch }]} />
      ) : (
        <Text
          style={[
            styles.toolbarButtonText,
            active && styles.toolbarButtonTextActive,
          ]}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
}

function RichTextEditorToolbar({
  editor,
  variant,
  showMore,
  onToggleMore,
  onRequestLink,
  onInsertImage,
  insertImageBusy,
  toolbarLayout,
  toolbarSet,
  editable,
}: {
  editor: Editor | null;
  variant: ToolbarVariant;
  showMore: boolean;
  onToggleMore: () => void;
  onRequestLink: () => void;
  onInsertImage: () => void;
  insertImageBusy: boolean;
  toolbarLayout?: TextAreaToolbarLayout;
  toolbarSet?: TextAreaToolbarSet;
  /** The host's read-only switch; an open flyout closes when it flips off. */
  editable?: boolean;
}): React.JSX.Element {
  const [, setTick] = useState(0);
  const [showHeadingPicker, setShowHeadingPicker] = useState(false);
  const isProduction = variant === 'production';
  const tid = (id: string) => toolbarTestId(variant, id);

  useEffect(() => {
    if (!editor) {
      return;
    }
    const refresh = () => setTick(value => value + 1);
    editor.on('selectionUpdate', refresh);
    editor.on('transaction', refresh);
    return () => {
      editor.off('selectionUpdate', refresh);
      editor.off('transaction', refresh);
    };
  }, [editor]);

  const activeFontSize =
    (editor?.getAttributes('textStyle').fontSize as string | undefined) ?? null;
  const activeFontFamily =
    (editor?.getAttributes('textStyle').fontFamily as string | undefined) ??
    null;
  const activeColor =
    (editor?.getAttributes('textStyle').color as string | undefined) ?? null;

  const closeHeadingPicker = () => setShowHeadingPicker(false);

  if (isProduction) {
    return (
      <TextAreaToolbar
        testIdPrefix="pd-richtext"
        layout={toolbarLayout}
        set={toolbarSet}
        editable={editable}
        showHeadingPicker={showHeadingPicker}
        imageBusy={insertImageBusy}
        state={{
          canUndo: editor?.can().undo() ?? false,
          canRedo: editor?.can().redo() ?? false,
          isBoldActive: editor?.isActive('bold') ?? false,
          isItalicActive: editor?.isActive('italic') ?? false,
          isUnderlineActive: editor?.isActive('underline') ?? false,
          isStrikeActive: editor?.isActive('strike') ?? false,
          isCodeActive: editor?.isActive('code') ?? false,
          isBulletListActive: editor?.isActive('bulletList') ?? false,
          isOrderedListActive: editor?.isActive('orderedList') ?? false,
          isLinkActive: editor?.isActive('link') ?? false,
          canSetLink:
            editor?.can().setLink({ href: 'https://example.com' }) ?? false,
          isSubscriptActive: editor?.isActive('subscript') ?? false,
          isSuperscriptActive: editor?.isActive('superscript') ?? false,
          isTableActive: editor?.isActive('table') ?? false,
          isBlockquoteActive: editor?.isActive('blockquote') ?? false,
          isCodeBlockActive: editor?.isActive('codeBlock') ?? false,
          activeFontSize:
            (editor?.getAttributes('textStyle').fontSize as
              | string
              | undefined) ?? undefined,
          activeColor:
            (editor?.getAttributes('textStyle').color as string | undefined) ??
            undefined,
          activeHighlight:
            (editor?.getAttributes('highlight').color as string | undefined) ??
            undefined,
          activeTextAlign: editor?.isActive({ textAlign: 'center' })
            ? 'center'
            : editor?.isActive({ textAlign: 'right' })
            ? 'right'
            : editor?.isActive({ textAlign: 'justify' })
            ? 'justify'
            : 'left',
          headingLevel: editor?.isActive('heading', { level: 1 })
            ? 1
            : editor?.isActive('heading', { level: 2 })
            ? 2
            : editor?.isActive('heading', { level: 3 })
            ? 3
            : undefined,
        }}
        actions={{
          onUndo: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .undo()
              .run(),
          onRedo: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .redo()
              .run(),
          onToggleHeadingPicker: () => setShowHeadingPicker(value => !value),
          onSetParagraph: () => {
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .setParagraph()
              .run();
            closeHeadingPicker();
          },
          onToggleHeading: level => {
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleHeading({ level })
              .run();
            closeHeadingPicker();
          },
          onToggleBulletList: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleBulletList()
              .run(),
          onToggleOrderedList: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleOrderedList()
              .run(),
          onIndent: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .sinkListItem('listItem')
              .run(),
          onOutdent: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .liftListItem('listItem')
              .run(),
          onBold: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleBold()
              .run(),
          onItalic: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleItalic()
              .run(),
          onStrike: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleStrike()
              .run(),
          onCode: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleCode()
              .run(),
          onUnderline: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleUnderline()
              .run(),
          onClearFormatting: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .clearNodes()
              .unsetAllMarks()
              .run(),
          onLink: onRequestLink,
          onSuperscript: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleSuperscript()
              .run(),
          onSubscript: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleSubscript()
              .run(),
          onSetFontSize: size =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .setFontSize(size)
              .run(),
          // "Default size": the `fontSize` attribute off the text style, and
          // an emptied `<span>` removed with it (`tiptapFontSize.ts`).
          onUnsetFontSize: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .unsetFontSize()
              .run(),
          onAlign: align =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .setTextAlign(align)
              .run(),
          onInsertImage: onInsertImage,
          onSetColor: color =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .setColor(color)
              .run(),
          onUnsetColor: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .unsetColor()
              .run(),
          onToggleHighlight: color =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleHighlight({ color })
              .run(),
          onInsertTable: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run(),
          // The two message-set blocks (#1530 Teams parity): the same
          // StarterKit commands the lab toolbar below already runs.
          onToggleBlockquote: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleBlockquote()
              .run(),
          onToggleCodeBlock: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleCodeBlock()
              .run(),
          onAddTableRow: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .addRowAfter()
              .run(),
          onDeleteTableRow: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .deleteRow()
              .run(),
          onAddTableColumn: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .addColumnAfter()
              .run(),
          onDeleteTableColumn: () =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .deleteColumn()
              .run(),
        }}
      />
    );
  }

  return (
    <View testID={tid('toolbar')}>
      <View style={styles.toolbar}>
        <ToolbarButton
          testID={tid('bold')}
          label="B"
          accessibilityLabel="Bold"
          active={editor?.isActive('bold')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleBold()
              .run()
          }
        />
        <ToolbarButton
          testID={tid('italic')}
          label="I"
          accessibilityLabel="Italic"
          active={editor?.isActive('italic')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleItalic()
              .run()
          }
        />
        <ToolbarButton
          testID={tid('underline')}
          label="U"
          accessibilityLabel="Underline"
          active={editor?.isActive('underline')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleUnderline()
              .run()
          }
        />
        <ToolbarButton
          testID={tid('strike')}
          label="S"
          accessibilityLabel="Strikethrough"
          active={editor?.isActive('strike')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleStrike()
              .run()
          }
        />
        <View style={styles.toolbarDivider} />
        <ToolbarButton
          testID={tid('bullet')}
          label="•"
          accessibilityLabel="Bulleted list"
          active={editor?.isActive('bulletList')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleBulletList()
              .run()
          }
        />
        <ToolbarButton
          testID={tid('ordered')}
          label="1."
          accessibilityLabel="Numbered list"
          active={editor?.isActive('orderedList')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleOrderedList()
              .run()
          }
        />
        <View style={styles.toolbarDivider} />
        {TEXT_COLORS.slice(0, 3).map(color => (
          <ToolbarButton
            key={color.id}
            testID={tid(`color-${color.id}`)}
            label={color.label}
            accessibilityLabel={`Text color ${color.label}`}
            active={activeColor?.toLowerCase() === color.value.toLowerCase()}
            colorSwatch={color.value}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .setColor(color.value)
                .run()
            }
          />
        ))}
        {FONT_SIZE_PRESETS.slice(0, 3).map(size => (
          <ToolbarButton
            key={size}
            testID={tid(`size-${size.replace('px', '')}`)}
            label={size.replace('px', '')}
            accessibilityLabel={`Font size ${size}`}
            active={activeFontSize === size}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .setFontSize(size)
                .run()
            }
          />
        ))}
        <View style={styles.toolbarDivider} />
        <ToolbarButton
          testID={tid('blockquote')}
          label="Quote"
          accessibilityLabel="Blockquote"
          active={editor?.isActive('blockquote')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleBlockquote()
              .run()
          }
        />
        <ToolbarButton
          testID={tid('link')}
          label="Link"
          accessibilityLabel="Insert or remove link"
          active={editor?.isActive('link')}
          onPress={onRequestLink}
        />
        <ToolbarButton
          testID={tid('code')}
          label="<>"
          accessibilityLabel="Inline code"
          active={editor?.isActive('code')}
          onPress={() =>
            editor
              ?.chain()
              .focus(undefined, { scrollIntoView: false })
              .toggleCode()
              .run()
          }
        />
        <ToolbarButton
          testID={tid('more')}
          label="···"
          accessibilityLabel="Show more formatting tools"
          active={showMore}
          onPress={onToggleMore}
        />
      </View>

      {!isProduction && showMore ? (
        <View
          style={[styles.toolbar, styles.toolbarMore]}
          testID={tid('toolbar-more')}
        >
          <ToolbarButton
            testID={tid('undo')}
            label="Undo"
            accessibilityLabel="Undo"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .undo()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('redo')}
            label="Redo"
            accessibilityLabel="Redo"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .redo()
                .run()
            }
          />
          <View style={styles.toolbarDivider} />
          <ToolbarButton
            testID={tid('paragraph')}
            label="P"
            accessibilityLabel="Paragraph"
            active={editor?.isActive('paragraph')}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .setParagraph()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('h1')}
            label="H1"
            accessibilityLabel="Heading 1"
            active={editor?.isActive('heading', { level: 1 })}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .toggleHeading({ level: 1 })
                .run()
            }
          />
          <ToolbarButton
            testID={tid('h2')}
            label="H2"
            accessibilityLabel="Heading 2"
            active={editor?.isActive('heading', { level: 2 })}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .toggleHeading({ level: 2 })
                .run()
            }
          />
          <ToolbarButton
            testID={tid('h3')}
            label="H3"
            accessibilityLabel="Heading 3"
            active={editor?.isActive('heading', { level: 3 })}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .toggleHeading({ level: 3 })
                .run()
            }
          />
          <View style={styles.toolbarDivider} />
          <ToolbarButton
            testID={tid('sub')}
            label="X₂"
            accessibilityLabel="Subscript"
            active={editor?.isActive('subscript')}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .toggleSubscript()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('sup')}
            label="X²"
            accessibilityLabel="Superscript"
            active={editor?.isActive('superscript')}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .toggleSuperscript()
                .run()
            }
          />
          <View style={styles.toolbarDivider} />
          {FONT_SIZE_PRESETS.map(size => (
            <ToolbarButton
              key={`more-${size}`}
              testID={tid(`size-more-${size.replace('px', '')}`)}
              label={size.replace('px', '')}
              accessibilityLabel={`Font size ${size}`}
              active={activeFontSize === size}
              onPress={() =>
                editor
                  ?.chain()
                  .focus(undefined, { scrollIntoView: false })
                  .setFontSize(size)
                  .run()
              }
            />
          ))}
          {FONT_FAMILY_PRESETS.map(font => (
            <ToolbarButton
              key={font.id}
              testID={tid(`font-${font.id}`)}
              label={font.label}
              accessibilityLabel={`Font family ${font.label}`}
              active={activeFontFamily === font.value}
              onPress={() =>
                editor
                  ?.chain()
                  .focus(undefined, { scrollIntoView: false })
                  .setFontFamily(font.value)
                  .run()
              }
            />
          ))}
          <View style={styles.toolbarDivider} />
          {TEXT_COLORS.map(color => (
            <ToolbarButton
              key={`more-color-${color.id}`}
              testID={tid(`color-more-${color.id}`)}
              label={color.label}
              accessibilityLabel={`Text color ${color.label}`}
              active={activeColor?.toLowerCase() === color.value.toLowerCase()}
              colorSwatch={color.value}
              onPress={() =>
                editor
                  ?.chain()
                  .focus(undefined, { scrollIntoView: false })
                  .setColor(color.value)
                  .run()
              }
            />
          ))}
          {HIGHLIGHT_COLORS.map(color => (
            <ToolbarButton
              key={color.id}
              testID={tid(`highlight-${color.id}`)}
              label={color.label}
              accessibilityLabel={color.label}
              active={editor?.isActive('highlight', { color: color.value })}
              colorSwatch={color.value}
              onPress={() =>
                editor
                  ?.chain()
                  .focus(undefined, { scrollIntoView: false })
                  .toggleHighlight({ color: color.value })
                  .run()
              }
            />
          ))}
          <View style={styles.toolbarDivider} />
          <ToolbarButton
            testID={tid('indent')}
            label="Indent"
            accessibilityLabel="Indent list item"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .sinkListItem('listItem')
                .run()
            }
          />
          <ToolbarButton
            testID={tid('outdent')}
            label="Outdent"
            accessibilityLabel="Outdent list item"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .liftListItem('listItem')
                .run()
            }
          />
          <ToolbarButton
            testID={tid('align-left')}
            label="Left"
            accessibilityLabel="Align left"
            active={editor?.isActive({ textAlign: 'left' })}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .setTextAlign('left')
                .run()
            }
          />
          <ToolbarButton
            testID={tid('align-center')}
            label="Center"
            accessibilityLabel="Align center"
            active={editor?.isActive({ textAlign: 'center' })}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .setTextAlign('center')
                .run()
            }
          />
          <ToolbarButton
            testID={tid('align-right')}
            label="Right"
            accessibilityLabel="Align right"
            active={editor?.isActive({ textAlign: 'right' })}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .setTextAlign('right')
                .run()
            }
          />
          <View style={styles.toolbarDivider} />
          <ToolbarButton
            testID={tid('hr')}
            label="HR"
            accessibilityLabel="Horizontal rule"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .setHorizontalRule()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('code-block')}
            label="Code"
            accessibilityLabel="Code block"
            active={editor?.isActive('codeBlock')}
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .toggleCodeBlock()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('toolbar-image')}
            label="Img"
            accessibilityLabel="Insert image via authenticated upload"
            disabled={insertImageBusy}
            onPress={onInsertImage}
          />
          <ToolbarButton
            testID={tid('clear')}
            label="Clear"
            accessibilityLabel="Clear formatting"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .clearNodes()
                .unsetAllMarks()
                .run()
            }
          />
          <View style={styles.toolbarDivider} />
          <ToolbarButton
            testID={tid('table-insert')}
            label="Tbl"
            accessibilityLabel="Insert 3 by 3 table"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
          />
          <ToolbarButton
            testID={tid('table-add-row')}
            label="+Row"
            accessibilityLabel="Add table row"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .addRowAfter()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('table-del-row')}
            label="-Row"
            accessibilityLabel="Delete table row"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .deleteRow()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('table-add-col')}
            label="+Col"
            accessibilityLabel="Add table column"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .addColumnAfter()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('table-del-col')}
            label="-Col"
            accessibilityLabel="Delete table column"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .deleteColumn()
                .run()
            }
          />
          <ToolbarButton
            testID={tid('table-delete')}
            label="DelTbl"
            accessibilityLabel="Delete table"
            onPress={() =>
              editor
                ?.chain()
                .focus(undefined, { scrollIntoView: false })
                .deleteTable()
                .run()
            }
          />
        </View>
      ) : null}
    </View>
  );
}

export function RichTextEditor({
  onResolveImage,
  initialHtml = '',
  // Lab chrome and the paste harness are opt-in: a diagnostics prop fails
  // safe, and no in-app caller omits it anyway.
  showDiagnostics = false,
  onStatusChange,
  onHtmlChange,
  placeholder,
  inlineImageFieldKey,
  toolbarLayout,
  toolbarSet,
  fillHeight,
  onSubmitShortcut,
  editable = true,
}: RichTextEditorProps): React.JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const seenIdsRef = useRef(new Set<string>());
  const recentPasteTransactionsRef = useRef(new Map<string, number>());
  const rehostInFlightRef = useRef(new Set<string>());
  const rehostTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const lastEmittedHtmlRef = useRef(initialHtml || '<p></p>');
  // Six-candidate parity metric — comparable to the candidate lab's
  // `pd-richtext-candidate-metrics` init timing (mount-to-ready), since
  // Tiptap/Quill aren't sandboxed frames and don't emit the same
  // postMessage `ready` telemetry.
  const mountedAtRef = useRef(performance.now());
  const [initMetrics, setInitMetrics] = useState<InitMetrics | null>(null);
  const setNoteRef = useRef<(message: string) => void>(() => undefined);
  const setErrorMessageRef = useRef<(message: string | null) => void>(
    () => undefined,
  );
  const configRef = useRef({
    onResolveImage,
    onStatusChange,
    onHtmlChange,
    inlineImageFieldKey,
    onSubmitShortcut,
    showDiagnostics,
  });
  configRef.current = {
    onResolveImage,
    onStatusChange,
    onHtmlChange,
    inlineImageFieldKey,
    onSubmitShortcut,
    showDiagnostics,
  };

  const scheduleInlineImageRehost = useCallback(() => {
    if (!editorRef.current) {
      return;
    }
    if (rehostTimerRef.current) {
      clearTimeout(rehostTimerRef.current);
    }
    rehostTimerRef.current = setTimeout(() => {
      rehostTimerRef.current = null;
      const activeEditor = editorRef.current;
      if (!activeEditor) {
        return;
      }
      const html = activeEditor.getHTML();
      if (!editorHtmlHasRehostableImages(html)) {
        return;
      }
      void rehostInlineImagesInEditor(
        activeEditor,
        configRef.current.onResolveImage,
        rehostInFlightRef.current,
      )
        .then(result => {
          if (result.failed > 0) {
            const message =
              'Some pasted images could not be saved. Try inserting them from the toolbar.';
            setNoteRef.current(message);
            setErrorMessageRef.current(message);
          } else {
            setErrorMessageRef.current(null);
          }
          emitPersistableEditorHtml(
            activeEditor,
            configRef.current.onHtmlChange,
            lastEmittedHtmlRef,
          );
        })
        .catch(() => {
          const message =
            'Some pasted images could not be saved. Try inserting them from the toolbar.';
          setNoteRef.current(message);
          setErrorMessageRef.current(message);
        });
    }, 250);
  }, []);
  const [busy, setBusy] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');
  const [pasteHarness, setPasteHarness] = useState<PasteHarnessSnapshot | null>(
    null,
  );
  const [showLinkRow, setShowLinkRow] = useState(false);
  const linkSelectionRef = useRef<{ from: number; to: number } | null>(null);
  const [note, setNote] = useState(
    'Teams/Loop-class toolbar + Word/email paste. Email layout tables are flattened on paste; inserted tables and code blocks are kept.',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  setNoteRef.current = setNote;
  setErrorMessageRef.current = setErrorMessage;

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    // Decided once, at mount: `useEditor` reads `extensions` on creation and
    // the set never changes for the life of a composer.
    extensions: buildWebEditorExtensions(toolbarSet),
    content: parkImgSrcsForDomParse(initialHtml || '<p></p>'),
    editorProps: {
      transformPastedHTML: (html: string) =>
        parkImgSrcsForDomParse(sanitizePaste(html, RICH_TEXT_PASTE_OPTIONS)),
      transformPasted: slice =>
        linkUrlsInSlice(stripEmptyListItemsFromSlice(slice)),
      // #1530: the send chord, read off the ref so the editor is not rebuilt
      // when the callback identity changes. `true` consumes the event.
      handleKeyDown: (_view, event) => {
        const submit = configRef.current.onSubmitShortcut;
        if (
          submit &&
          event.key === 'Enter' &&
          (event.metaKey || event.ctrlKey) &&
          !event.shiftKey &&
          !event.altKey
        ) {
          event.preventDefault();
          submit();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      emitPersistableEditorHtml(
        updatedEditor,
        onHtmlChange,
        lastEmittedHtmlRef,
      );
      const raw = restoreImageSrcsFromAttrs(
        updatedEditor.getHTML(),
        collectImageSrcAttrs(updatedEditor.state.doc),
      );
      if (editorHtmlHasRehostableImages(raw)) {
        scheduleInlineImageRehost();
      }
    },
    onCreate: ({ editor: created }) => {
      editorRef.current = created;
      setInitMetrics({
        initializedInMs: performance.now() - mountedAtRef.current,
        contentBytes: utf8ByteLength(sanitizeTextArea(created.getHTML())),
      });
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // `editable` is read once by `useEditor`; afterwards only `setEditable`
  // changes it. Re-applied on every flip, and on `editor` arriving, so the
  // first render after mount is not the only one that counts.
  useEffect(() => {
    if (!editor || editor.isEditable === editable) {
      return;
    }
    editor.setEditable(editable);
  }, [editor, editable]);

  useEffect(() => {
    if (!editor || !showLinkRow) {
      return undefined;
    }
    const rememberSelection = () => {
      const { from, to, empty } = editor.state.selection;
      if (!empty) {
        linkSelectionRef.current = { from, to };
      }
    };
    editor.on('selectionUpdate', rememberSelection);
    return () => {
      editor.off('selectionUpdate', rememberSelection);
    };
  }, [editor, showLinkRow]);

  useEffect(() => {
    if (!editor) {
      return;
    }
    if (editor.getHTML().includes('[Uploading image:')) {
      return;
    }
    if (
      !shouldReplaceEditorHtml({
        isFocused: editor.isFocused,
        currentHtml: editor.getHTML(),
        incomingHtml: initialHtml,
        lastEmittedHtml: lastEmittedHtmlRef.current,
      })
    ) {
      return;
    }
    const next = parkImgSrcsForDomParse(
      normalizeRichListStructure(initialHtml, { preserveEmptyBlocks: true }) ||
        '<p></p>',
    );
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, initialHtml]);

  const uploadImageAtPosition = useCallback(
    async (file: File, position: number, alt?: string) => {
      const config = configRef.current;
      const fieldKey = config.inlineImageFieldKey;
      const clientPasteId = `web_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      if (seenIdsRef.current.has(clientPasteId)) {
        return;
      }
      seenIdsRef.current.add(clientPasteId);

      if (fieldKey) {
        beginInlineImageAttempt(fieldKey);
      }
      setBusy(true);
      setErrorMessage(null);
      config.onStatusChange?.({ status: 'uploading', clientPasteId });

      const failWith = (message: string) => {
        setErrorMessage(message);
        setNote(message);
        config.onStatusChange?.({ status: 'error', clientPasteId, message });
      };

      let previewSrc: string | null = null;
      let previewRetained = false;
      try {
        if (!editor) {
          failWith('Image upload failed');
          return;
        }

        const resolution = await config.onResolveImage(file);
        if (!resolution.ok) {
          failWith(resolution.error);
          return;
        }

        previewSrc = URL.createObjectURL(file);
        rememberPendingInlineImagePreview(resolution.reference, previewSrc);
        previewRetained = true;

        const docSize = editor.state.doc.content.size;
        const insertPos =
          Number.isFinite(position) && position >= 0 && position <= docSize
            ? position
            : editor.state.selection.from;
        editor
          .chain()
          .focus(undefined, { scrollIntoView: false })
          .insertContentAt(insertPos, {
            type: 'image',
            attrs: {
              src: resolution.reference,
              alt: alt || file.name || 'attachment',
            },
          })
          .run();

        emitPersistableEditorHtml(
          editor,
          config.onHtmlChange,
          lastEmittedHtmlRef,
        );

        setErrorMessage(null);
        setNote(
          resolution.uploaded
            ? `Image uploaded (${resolution.reference}).`
            : resolution.reference.startsWith('sitescope-attachment-pending://')
            ? 'Image added — it will upload when you save the request'
            : 'Image staged offline — will upload when back online',
        );
        config.onStatusChange?.({
          status: 'resolved',
          clientPasteId,
          attachmentId: resolution.attachmentId,
          reference: resolution.reference,
        });
      } catch (error) {
        failWith(
          error instanceof Error ? error.message : 'Attachment upload failed',
        );
      } finally {
        if (previewSrc && !previewRetained) {
          URL.revokeObjectURL(previewSrc);
        }
        setBusy(false);
        if (fieldKey) {
          endInlineImageAttempt(fieldKey);
        }
      }
    },
    [editor],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }
    const handlePaste = (event: ClipboardEvent) => {
      const payload = readMixedEmailPaste(event, RICH_TEXT_PASTE_OPTIONS);
      const rawHtml = event.clipboardData?.getData('text/html') ?? '';
      const recordPasteHarness = (build: () => PasteHarnessSnapshot) => {
        // Lab only: a product editor never shows the readout, so it must not
        // pay the extra walks over the payload or the re-render either.
        if (
          !configRef.current.showDiagnostics ||
          typeof window === 'undefined' ||
          !isLocalPasteHarnessHost(window.location.hostname)
        ) {
          return;
        }
        try {
          setPasteHarness(build());
        } catch {
          // Harness must never block paste.
        }
      };
      recordPasteHarness(() => ({
        clipboardTypes:
          payload?.clipboardTypes ?? clipboardFlavorTypes(event.clipboardData),
        htmlLength: rawHtml.length,
        rtfLength:
          payload?.rtfLength ??
          (event.clipboardData?.getData('text/rtf') ?? '').length,
        imgSrcSchemes: clipboardImgSrcSchemes(promoteEmailImageSlots(rawHtml)),
        imageFiles: (payload?.images ?? []).map(file => ({
          name: file.name,
          type: file.type,
          size: file.size,
        })),
        stitched: 0,
        leftover: payload?.images.length ?? 0,
      }));
      if (!payload) {
        return;
      }
      const needsOutlookBytes =
        htmlHasUnresolvedOutlookImages(rawHtml) && payload.images.length === 0;
      const insertClipboardFiles = shouldInsertClipboardImageFiles(
        payload.html,
        payload.images.length,
      );
      const hasRehostableHtml = editorHtmlHasRehostableImages(payload.html);
      if (
        !insertClipboardFiles &&
        !hasRehostableHtml &&
        !needsOutlookBytes &&
        payload.images.length === 0
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (
        !acceptPasteTransaction(
          recentPasteTransactionsRef.current,
          payload.transactionId,
        )
      ) {
        return;
      }
      const applyPaste = (images: File[]) => {
        const htmlSource = promoteEmailImageSlots(
          htmlToStitchOnPaste(rawHtml, payload.html),
        );
        const previews = images.map(file => URL.createObjectURL(file));
        const stitched = stitchClipboardImagesIntoHtml(htmlSource, previews);
        const unusedPreviews = previews.slice(stitched.used);
        const underTable = payload.keepImagesBesideTable
          ? previewsToPlaceUnderTable(
              stitched.html,
              unusedPreviews,
              payload.embeddedImageCount,
              stitched.used,
            )
          : [];
        const placeUnderTable = underTable.length > 0;
        unusedPreviews.forEach(src => {
          if (!underTable.includes(src)) {
            URL.revokeObjectURL(src);
          }
        });
        const leftover = placeUnderTable ? [] : images.slice(stitched.used);
        // Chrome "Copy image": an `<img src="https://…">` AND the same picture
        // as a file. Image-only HTML with real clipboard files and NO Outlook
        // slot stitched means the files are the pictures, so the tags go and
        // the files insert once through the leftover path, each keeping the
        // dropped tag's alt (#1530). `stitched.used === 0` is part of the
        // shape: once pass 1 has matched a file to a `cid:` slot, the
        // remaining files are that email's, not a copy of the remote logo.
        // A Word table picture (`placeUnderTable`) never takes this path:
        // leftover is empty, so the tags stay and the RTF pict is appended
        // under the table (#1588).
        const covered =
          insertClipboardFiles &&
          stitched.used === 0 &&
          images.length > 0 &&
          isImageOnlyPasteHtml(htmlSource)
            ? dropImgsCoveredByClipboardFiles(
                stitched.html,
                leftover.length,
                isRehostableImageSrc,
              )
            : { html: stitched.html, dropped: 0, alts: [] };
        const sanitized = sanitizePaste(
          placeUnderTable
            ? appendImagesAfterLastTable(covered.html, underTable)
            : covered.html,
          RICH_TEXT_PASTE_OPTIONS,
        );
        const html =
          recoverWordTablePaste({
            sanitizedHtml: sanitized,
            rtf: payload.rtf,
          }) ?? sanitized;
        recordPasteHarness(() => ({
          clipboardTypes: payload.clipboardTypes,
          htmlLength: rawHtml.length,
          rtfLength: payload.rtfLength,
          imgSrcSchemes: clipboardImgSrcSchemes(
            promoteEmailImageSlots(rawHtml),
          ),
          imageFiles: images.map(file => ({
            name: file.name,
            type: file.type,
            size: file.size,
          })),
          stitched: stitched.used,
          leftover: leftover.length,
        }));
        if (!isBlankPasteHtml(html)) {
          editor
            .chain()
            .focus(undefined, { scrollIntoView: false })
            .insertContent(parkImgSrcsForDomParse(html))
            .run();
        }
        if (editorHtmlHasRehostableImages(html)) {
          scheduleInlineImageRehost();
        }
        const skipLeftoverBecauseHtmlAlreadyHasPixels =
          pasteHtmlContainsEmbeddedImage(html) && stitched.used === 0;
        const skipTableSnapshot =
          !payload.keepImagesBesideTable &&
          (shouldDropClipboardSnapshotBesideTable(html) ||
            shouldDropClipboardSnapshotBesideTable(
              htmlToStitchOnPaste(rawHtml, html),
            ));
        if (
          leftover.length > 0 &&
          !skipLeftoverBecauseHtmlAlreadyHasPixels &&
          !skipTableSnapshot
        ) {
          void leftover.reduce(async (previous, file, index) => {
            await previous;
            await uploadImageAtPosition(
              file,
              editor.state.selection.from,
              covered.alts.at(index),
            );
          }, Promise.resolve());
        }
      };
      if (!needsOutlookBytes) {
        applyPaste(payload.images);
        return;
      }
      void supplementOutlookClipboardImages(event.clipboardData).then(extra =>
        applyPaste([...payload.images, ...extra]),
      );
    };
    editor.view.dom.addEventListener('paste', handlePaste, true);
    return () => {
      editor.view.dom.removeEventListener('paste', handlePaste, true);
      if (rehostTimerRef.current) {
        clearTimeout(rehostTimerRef.current);
        rehostTimerRef.current = null;
      }
    };
  }, [editor, scheduleInlineImageRehost, uploadImageAtPosition]);

  const handleRequestLink = useCallback(() => {
    if (!editor) {
      return;
    }
    if (editor.isActive('link')) {
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .unsetLink()
        .run();
      setShowLinkRow(false);
      setLinkDraft('');
      return;
    }
    const { from, to, empty } = editor.state.selection;
    linkSelectionRef.current = empty ? null : { from, to };
    setShowLinkRow(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    const trimmed = linkDraft.trim();
    if (!editor || !trimmed) {
      return;
    }
    const href = autoLinkMatch(trimmed)?.href ?? trimmed;
    const current = editor.state.selection;
    const range = current.empty
      ? linkSelectionRef.current
      : { from: current.from, to: current.to };
    if (!linkEditorRange(editor, href, range)) {
      return;
    }
    linkSelectionRef.current = null;
    setShowLinkRow(false);
    setLinkDraft('');
  }, [editor, linkDraft]);

  const openImagePicker = useCallback(() => {
    if (busy) {
      return;
    }
    fileInputRef.current?.click();
  }, [busy]);

  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) {
      return undefined;
    }
    const onNativeChange = () => {
      const file = input.files?.[0];
      const activeEditor = editorRef.current;
      if (!file || !activeEditor) {
        return;
      }
      uploadImageAtPosition(file, activeEditor.state.selection.from).catch(
        () => {
          setNote('Unexpected browser upload failure');
        },
      );
      input.value = '';
    };
    input.addEventListener('change', onNativeChange);
    return () => input.removeEventListener('change', onNativeChange);
  }, [uploadImageAtPosition]);

  const toolbarVariant: ToolbarVariant = showDiagnostics ? 'lab' : 'production';
  if (editor) {
    editorRef.current = editor;
  }

  return (
    <View
      style={[styles.wrap, fillHeight ? styles.wrapFillHeight : null]}
      testID="pd-richtext-editor"
    >
      {showDiagnostics ? <Text style={styles.label}>Tiptap 3.29.1</Text> : null}
      <View style={styles.toolbarChrome}>
        <RichTextEditorToolbar
          editor={editor}
          variant={toolbarVariant}
          toolbarLayout={toolbarLayout}
          toolbarSet={toolbarSet}
          editable={editable}
          showMore={showMore}
          onToggleMore={() => setShowMore(value => !value)}
          onRequestLink={handleRequestLink}
          onInsertImage={openImagePicker}
          insertImageBusy={busy}
        />
      </View>
      {showLinkRow ? (
        <View style={styles.linkRow}>
          <TextInput
            testID={toolbarTestId(toolbarVariant, 'link-input')}
            value={linkDraft}
            onChangeText={setLinkDraft}
            onSubmitEditing={applyLink}
            placeholder="https://example.com"
            placeholderTextColor={colors.inputPlaceholder}
            autoCapitalize="none"
            autoCorrect={false}
            style={styles.linkInput}
          />
          <LinkApplyButton
            onApply={applyLink}
            testID={toolbarTestId(toolbarVariant, 'link-apply')}
          />
        </View>
      ) : null}
      <style>{TIPTAP_TABLE_CSS}</style>
      <View
        testID="pd-richtext-editor-box"
        style={[
          styles.editorFrame,
          fillHeight ? styles.editorFrameFillHeight : null,
        ]}
      >
        <div
          data-testid="pd-richtext-spike-first-editor"
          className="ss-tiptap-danger-room"
          style={fillHeight ? WEB_EDITOR_FILL_HEIGHT_STYLE : WEB_EDITOR_STYLE}
        >
          {editor?.isEmpty && placeholder ? (
            <span
              style={WEB_PLACEHOLDER_STYLE}
              data-testid="pd-richtext-placeholder"
            >
              {placeholder}
            </span>
          ) : null}
          <EditorContent editor={editor} />
        </div>
      </View>
      {showDiagnostics && pasteHarness ? (
        <Text style={styles.harness} testID="pd-richtext-paste-harness">
          {`Paste harness · types: ${
            pasteHarness.clipboardTypes.join(', ') || 'none'
          } · HTML ${pasteHarness.htmlLength} chars · RTF ${
            pasteHarness.rtfLength
          } · img src: ${summarizeImgSrcSchemes(
            pasteHarness.imgSrcSchemes,
          )} · files: ${
            pasteHarness.imageFiles.length
              ? pasteHarness.imageFiles
                  .map(
                    file =>
                      `${file.name} ${file.type || 'unknown'} ${file.size}b`,
                  )
                  .join('; ')
              : 'none'
          } · stitched ${pasteHarness.stitched} · leftover ${
            pasteHarness.leftover
          }`}
        </Text>
      ) : null}
      <View
        testID="pd-richtext-editor-bottom-edge"
        style={styles.editorBottomEdge}
      />
      {showDiagnostics ? (
        <Text style={styles.note} testID="pd-richtext-spike-first-metrics">
          {initMetrics
            ? `${initMetrics.initializedInMs.toFixed(1)} ms init · ${
                initMetrics.contentBytes
              } content bytes (post-sanitize)`
            : 'Waiting for runtime'}
        </Text>
      ) : null}
      {errorMessage ? (
        <Text
          style={styles.errorNote}
          accessibilityRole="alert"
          testID="pd-richtext-inline-error"
        >
          {errorMessage}
        </Text>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept={INLINE_PHOTO_ACCEPT_ATTR}
        data-testid={
          showDiagnostics ? 'pd-richtext-spike-first-file' : 'pd-richtext-file'
        }
        style={HIDDEN_FILE_INPUT_STYLE}
      />
      {showDiagnostics ? (
        <Text style={styles.note} testID="pd-richtext-spike-first-note">
          {note}
        </Text>
      ) : null}
    </View>
  );
}

const TIPTAP_TABLE_CSS = `
.ss-tiptap-danger-room .ProseMirror {
  outline: none;
  min-height: 160px;
  background: transparent;
  /* Let the outer scrollport clip; do not grow the flex parent. */
  max-width: 100%;
}
.ss-tiptap-danger-room .ProseMirror:focus {
  outline: none;
}
.ss-tiptap-danger-room .ProseMirror *:focus {
  outline: none;
}
.ss-tiptap-danger-room .ProseMirror-selectednode {
  outline: none;
}
${richHtmlScopedCss('.ss-tiptap-danger-room .ProseMirror')}
`;

const WEB_EDITOR_STYLE: React.CSSProperties = {
  ...RICH_TEXT_WEB_SCROLLPORT_STYLE,
  minHeight: 180,
  maxHeight: 420,
  position: 'relative',
  padding: 8,
  backgroundColor: colors.backgroundPrimary,
  borderBottomLeftRadius: 4,
  borderBottomRightRadius: 4,
};
/** Flex host (#1149 Issue 5): absorb leftover height; only this scrollport scrolls. */
const WEB_EDITOR_FILL_HEIGHT_STYLE: React.CSSProperties = {
  ...WEB_EDITOR_STYLE,
  flex: 1,
  minHeight: 0,
  maxHeight: 'none',
};
const WEB_PLACEHOLDER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 8,
  left: 8,
  right: 8,
  color: colors.inputPlaceholder,
  pointerEvents: 'none',
  fontSize: fontSize.formControl,
  lineHeight: `${lineHeight.formControl}px`,
};
const HIDDEN_FILE_INPUT_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    alignSelf: 'stretch',
    backgroundColor: 'transparent',
    position: 'relative',
  },
  wrapFillHeight: {
    minHeight: 0,
  },
  toolbarChrome: {
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    backgroundColor: colors.backgroundPrimary,
    overflow: 'hidden',
  },
  editorFrame: {
    position: 'relative',
    minWidth: 0,
    maxWidth: '100%',
    alignSelf: 'stretch',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderColor: colors.inputBorderNeutral,
    backgroundColor: colors.backgroundPrimary,
    overflow: 'hidden',
  },
  editorBottomEdge: {
    height: 1,
    alignSelf: 'stretch',
    backgroundColor: colors.inputBorderNeutral,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  editorFrameFillHeight: {
    flex: 1,
    minHeight: 0,
  },
  loadingStatus: {
    minHeight: 32,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.backgroundPrimary,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: colors.inputBorderNeutral,
  },
  loadingStatusText: {
    color: colors.textSecondary,
    fontSize: 14,
  },
  label: {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 6,
    color: '#111827',
  },
  toolbar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    padding: 6,
    backgroundColor: colors.backgroundPrimary,
  },
  toolbarMore: {
    backgroundColor: '#EEF2FF',
  },
  toolbarHeadingPicker: {
    backgroundColor: '#F3F4F6',
  },
  toolbarDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: '#D1D5DB',
    marginHorizontal: 2,
  },
  toolbarButton: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 6,
  },
  toolbarButtonActive: {
    backgroundColor: 'rgba(255, 107, 53, 0.12)',
  },
  toolbarButtonDisabled: {
    opacity: 0.45,
  },
  toolbarButtonText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  toolbarButtonTextActive: {
    color: '#FF6B35',
  },
  colorSwatch: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#9CA3AF',
  },
  linkRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
    alignItems: 'center',
  },
  linkInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
    color: colors.textPrimary,
    ...Platform.select({
      web: {
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
      },
    }),
  },
  note: {
    marginTop: 6,
    fontSize: 12,
    color: '#374151',
  },
  harness: {
    marginTop: 8,
    fontSize: 11,
    lineHeight: 16,
    color: '#374151',
    backgroundColor: '#F3F4F6',
    padding: 8,
    fontFamily: 'Courier New, monospace',
  },
  errorNote: {
    marginTop: 6,
    fontSize: 12,
    color: colors.error,
  },
});
